// Endpoints RTK Query da feature queue. Tipos manuais espelhando contrato do
// QueueModule (Onda 3). FIXME: migrar pra paths['/v1/queue'] quando openapi-typescript
// regenerar.
import { api } from '@/lib/api';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { Role } from '@/config/constants';
import type { StoreColor } from '@/features/stores';
import { formatBRL } from '@/lib/utils/formatters';

/** Acima deste tempo de espera o card é tratado como "parado" (stale). Espelha
 *  URGENT_WAIT_MIN do backend (queue.service.ts). */
const STALE_WAIT_MIN = 15;

// RF-18: QUEUE (`aguardando`) e ASSIGNED (`aguardando_atendimento`) são um único
// filtro `aguardando` = "Aguardando atendimento". `aguardando_atendimento` segue
// como status de card (distingue o card ASSIGNED), mas não é mais uma aba.
//
// `janela_expirada` é transversal e EXCLUSIVO: passadas as 24h não há atendimento
// nenhum em curso (a Meta só aceita template), então a conversa sai das abas de
// atendimento e fica esperando um encerramento manual.
export type QueueFilter =
  | 'all'
  | 'em_atendimento'
  | 'aguardando'
  | 'em_atendimento_ia'
  | 'janela_expirada';

export type QueueCardStatus = 'em_atendimento' | 'aguardando' | 'aguardando_atendimento';

/**
 * Quem está atendendo um card `em_atendimento` (ausente em `aguardando`).
 *
 * Até 22/09 havia `externo` (a loja respondendo pelo celular, conversa ACTIVE sem dono).
 * Saiu: a mensagem do celular agora manda a conversa para "Aguardando atendimento", e
 * "Em atendimento" exige alguém do sistema ter assumido.
 */
export type QueueCardAttendant = { type: 'ia' } | { type: 'human'; name: string; tone: AvatarTone };

export interface QueueStoreSummary {
  id: string;
  name: string;
  color: StoreColor;
}

export interface QueueQuoteSummary {
  /** Total já formatado (BRL). */
  total: string;
  /** Resumo curto dos itens, ex: "200 cartões + 50 panfletos". */
  items: string;
}

export interface QueueCardDto {
  conversationId: string;
  contact: {
    id: string;
    name: string;
    avatarTone: AvatarTone;
    initials: string;
    company: string | null;
    /** Tags/segmentação manual do contato. */
    tags: string[];
  };
  store: QueueStoreSummary;
  status: QueueCardStatus;
  /**
   * Quem atende, em `em_atendimento`: IA ou vendedor humano. `null` em `aguardando`, e
   * o card trata `null` (ou um tipo que não conhece) como "não sei", nunca como IA.
   */
  attendant: QueueCardAttendant | null;
  productTag: string | null;
  /** Tag do motivo do handoff da IA (aguardando vendedor / precisa humano). */
  handoffTag: string | null;
  snippet: string;
  /**
   * ISO da ABERTURA da conversa. Cru, não pré-formatado: quem decide o formato é o
   * card, porque só ele sabe o estado — e foi o formato decidido AQUI
   * (`formatBubbleTime`, "HH:mm" sem data) que fez um card de ontem parecer de hoje.
   */
  arrivedAt: string;
  /** ISO — desde quando o cliente espera. Mesma âncora de `waitMin`. */
  waitingSince: string;
  waitMin: number;
  urgent: boolean;
  stale: boolean;
  /** Score IA (0..100). */
  score: number | null;
  quote: QueueQuoteSummary | null;
  /** Etapas IA — { done, total }. */
  aiSteps: { done: number; total: number } | null;
  /** Custo de IA da conversa em USD (já em dólar). null quando sem custo ou
   *  papel sem permissão de ver. */
  aiCostUsd: number | null;
  /** Janela de 24h vencida: ninguém está atendendo, só falta encerrar. */
  windowExpired: boolean;
  /** Veio do despejo de histórico do WhatsApp (onboarding do número), não de atendimento. */
  importado: boolean;
  /** Quando a janela fecha/fechou — ISO (o card calcula "fechou há X"). */
  windowClosesAt: string | null;
  /** RF-13: janela morreu sem nenhuma resposta nossa → "Não atendido" (ISO). */
  unansweredAt: string | null;
  /** Optimistic lock — exigido para concluir direto da Fila. */
  version: number;
}

export interface QueueDto {
  filters: {
    all: number;
    em_atendimento: number;
    em_atendimento_ia: number;
    aguardando: number;
    janela_expirada: number;
  };
  data: QueueCardDto[];
}

export interface QueueArgs {
  filter: QueueFilter;
  storeId?: string;
}

export interface OnlineVendorDto {
  id: string;
  name: string;
  tone: AvatarTone;
  role: Role;
  storeId: string | null;
  lastSeenAt: string;
}

export interface OnlineVendorsDto {
  data: OnlineVendorDto[];
}

export interface AssumeError {
  code: 'ALREADY_CLAIMED' | 'NOT_FOUND' | 'FORBIDDEN' | string;
  message: string;
}

function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// --------------------------------------------------------------------------
// Contrato bruto do backend (QueueModule). Difere do shape que a UI consome
// (cents/ISO/`id`/`tone`), então mapeamos no transformResponse abaixo.
// --------------------------------------------------------------------------

interface RawQueueQuoteItem {
  description: string;
  qty: number;
  unitPriceCents: number;
}

export interface RawQueueCard {
  id: string;
  contact: {
    id: string;
    name: string;
    initials: string;
    tone: number;
    phone: string;
    company?: string;
    tags?: string[];
  };
  store: { id: string; name: string; color: string };
  status: QueueCardStatus;
  attendant?: QueueCardAttendant;
  arrivedAt: string;
  /** Pode faltar enquanto a API antiga estiver no ar (deploy em voo). */
  waitingSince?: string;
  waitMin: number;
  snippet: string;
  productTag?: string;
  handoffTag?: string;
  quote?: { totalCents: number; itemsCount: number; items: RawQueueQuoteItem[] };
  aiSteps: { done: number; total: number };
  score: number;
  urgent: boolean;
  /** Sempre presente no JSON: número = custo USD; null = sem custo ou papel < SUPERVISOR. */
  aiCostUsd: number | null;
  windowExpired: boolean;
  importado?: boolean;
  windowClosesAt: string | null;
  unansweredAt: string | null;
  version: number;
}

interface RawQueueResponse {
  filters: QueueDto['filters'];
  data: RawQueueCard[];
}

/**
 * Mapper compartilhado com o Pipeline do CRM (`features/crm/pipeline.api`), que
 * mostra as MESMAS conversas por etapa do funil. Duas versões deste mapeamento
 * divergiriam no primeiro ajuste — e a divergência apareceria como "na Fila o
 * orçamento aparece formatado, no Pipeline não".
 */
export function mapQueueCard(raw: RawQueueCard): QueueCardDto {
  return {
    conversationId: raw.id,
    contact: {
      id: raw.contact.id,
      name: raw.contact.name,
      initials: raw.contact.initials,
      avatarTone: (raw.contact.tone as AvatarTone) ?? 1,
      company: raw.contact.company ?? null,
      tags: raw.contact.tags ?? [],
    },
    store: raw.store as QueueStoreSummary,
    status: raw.status,
    attendant: raw.attendant ?? null,
    productTag: raw.productTag ?? null,
    handoffTag: raw.handoffTag ?? null,
    snippet: raw.snippet,
    arrivedAt: raw.arrivedAt,
    // `?? arrivedAt`: cobre a janela em que o web sobe antes da API. Cai na
    // abertura, que é o mesmo fallback que o back usa quando não há `queuedAt`.
    waitingSince: raw.waitingSince ?? raw.arrivedAt,
    waitMin: raw.waitMin,
    urgent: raw.urgent,
    // Backend não envia `stale`; derivamos pelo mesmo limiar usado lá.
    stale: raw.waitMin > STALE_WAIT_MIN,
    score: raw.score,
    quote: raw.quote
      ? {
          total: formatBRL(raw.quote.totalCents),
          items: raw.quote.items.map((it) => it.description).join(' · '),
        }
      : null,
    aiSteps: raw.aiSteps ?? null,
    // USD já vem em dólar — guardamos o number cru, a formatação é na UI.
    aiCostUsd: raw.aiCostUsd ?? null,
    windowExpired: raw.windowExpired ?? false,
    // Sem o mapeamento o campo é descartado em silêncio no transformResponse — foi por
    // isso que o selo "Onboarding" não apareceu na primeira tentativa.
    importado: raw.importado ?? false,
    windowClosesAt: raw.windowClosesAt ?? null,
    unansweredAt: raw.unansweredAt ?? null,
    version: raw.version ?? 0,
  };
}

export const queueApi = api.injectEndpoints({
  endpoints: (build) => ({
    queue: build.query<QueueDto, QueueArgs>({
      query: ({ filter, storeId }) => `/queue${qs({ filter, storeId })}`,
      transformResponse: (raw: RawQueueResponse): QueueDto => ({
        filters: raw.filters,
        data: raw.data.map(mapQueueCard),
      }),
      providesTags: [{ type: 'Conversation', id: 'QUEUE' }],
    }),
    vendedoresOnline: build.query<OnlineVendorsDto, void>({
      query: () => '/vendedores/online',
    }),
    assume: build.mutation<{ conversationId: string }, string>({
      query: (conversationId) => ({
        url: `/queue/${conversationId}/assume`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, conversationId) => [
        { type: 'Conversation', id: 'QUEUE' },
        { type: 'Conversation', id: 'LIST' },
        { type: 'Conversation', id: conversationId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useQueueQuery, useVendedoresOnlineQuery, useAssumeMutation } = queueApi;
